import type {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  ResetPasswordRequest,
} from "../types/auth";

const BASE_URL = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_BASE_URL || "");

/**
 * Helper function to get API URL
 */
const getApiUrl = (endpoint: string): string => {
  if (import.meta.env.DEV && !BASE_URL) {
    // In development with no baseUrl, use relative URL to leverage Vite proxy
    return endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  }
  return `${BASE_URL}${endpoint}`;
};

/**
 * User Service for authentication-related API calls
 */
export class UserService {
  private static async postJson<T>(
    endpoint: string,
    body: unknown
  ): Promise<T> {
    const url = getApiUrl(endpoint);
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (response.status === 429) {
      return {
        success: false,
        message: "Too many requests. Please try again later.",
      } as unknown as T;
    }

    let parsed: unknown;
    try {
      parsed = await response.json();
    } catch {
      parsed = { success: false, message: "Invalid server response." };
    }
    return parsed as T;
  }

  /**
   * Request OTP for password reset.
   * Backend always returns a generic success message to avoid email enumeration.
   */
  static async ForgotPassword(
    request: ForgotPasswordRequest
  ): Promise<ForgotPasswordResponse> {
    try {
      return await UserService.postJson<ForgotPasswordResponse>(
        "/pabws/users/forgot_password",
        { email: request.email }
      );
    } catch (error: unknown) {
      console.error(
        "ForgotPassword network error:",
        error instanceof Error ? error.message : "unknown"
      );
      return {
        success: false,
        message: "Network error. Please check your connection.",
      };
    }
  }

  /**
   * Validate OTP. On success returns a single-use reset_token (JWT).
   */
  static async ValidateCode(
    request: VerifyOtpRequest
  ): Promise<VerifyOtpResponse> {
    try {
      return await UserService.postJson<VerifyOtpResponse>(
        "/pabws/users/verify_otp",
        { email: request.email, code: request.code }
      );
    } catch (error: unknown) {
      console.error(
        "ValidateCode network error:",
        error instanceof Error ? error.message : "unknown"
      );
      return {
        success: false,
        message: "Network error. Please check your connection.",
      };
    }
  }

  /**
   * Consume the reset token and set a new password.
   */
  static async ResetPassword(
    request: ResetPasswordRequest
  ): Promise<ForgotPasswordResponse> {
    try {
      return await UserService.postJson<ForgotPasswordResponse>(
        "/pabws/users/reset_password",
        {
          reset_token: request.reset_token,
          new_password: request.new_password,
        }
      );
    } catch (error: unknown) {
      console.error(
        "ResetPassword network error:",
        error instanceof Error ? error.message : "unknown"
      );
      return {
        success: false,
        message: "Network error. Please check your connection.",
      };
    }
  }

  /**
   * Admin reset password by user ID
   * @param userId - The user ID to reset password for
   * @returns Promise with success/message shape
   */
  static async AdminResetPassword(
    userId: string
  ): Promise<ForgotPasswordResponse> {
    try {
      if (!userId) {
        return {
          success: false,
          message: "User ID is required",
        };
      }

      return await UserService.postJson<ForgotPasswordResponse>(
        `/pabws/users/admin_reset_password/${encodeURIComponent(userId)}`,
        {}
      );
    } catch (error: unknown) {
      console.error(
        "AdminResetPassword network error:",
        error instanceof Error ? error.message : "unknown"
      );
      return {
        success: false,
        message: "Network error. Please check your connection.",
      };
    }
  }
}
